import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { 
  ArrowRight, 
  Brain, 
  Target, 
  Users, 
  Zap, 
  Shield,
  Clock,
  Play,
  CheckCircle,
  ChevronDown,
  Heart,
  Flame,
  Star,
  Lock,
  X,
  Monitor,
  BookOpen,
  PenLine,
  Share2,
  MessageCircle
} from 'lucide-react';

// Images
const SHAYAMAL_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/vxkjfdze_new%20version.jpg";

// Athlete photos for reel - full color, no filters
const ATHLETE_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/261v8bgb_1741760091210.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/5qd7a9vk_1752470820330.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/kxnxziy3_1752470826864.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/muka72jj_1743408068034.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/lje1ytz0_1753682612276.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/0omqt7ej_1772510404793.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/f2cuuors_1751262300158.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/ut36ag5t_1732686848034.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/ecz3mmyt_1733123643194.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/d2a1gyla_1736757437492.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/gjjv633p_1732946779217.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/bcqja2xk_1751262300158.jpeg",
  "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/ew0ip1rd_1773115205554.jpeg",
];

// Image that needs center positioning (man and woman sitting on sofa)
const CENTER_POSITION_IMAGE = "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/5qd7a9vk_1752470820330.jpeg";

// Client logos
const LOGOS = {
  google: "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/m3g1v6dm_logo_google.png",
  meta: "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/mmmrh2qy_logo_meta.avif",
  ogilvy: "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/e7p3wqtr_logo%20-%20ogilvy.png",
  stanford: "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/41h8pbao_logo_%20stanfornd.png",
  cricket: "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/bc5u5qey_logo_Cricket_India_Crest.svg.png",
  deloitte: "https://customer-assets.emergentagent.com/job_peak-mastery/artifacts/ekioaqbc_logo_deloitte.png",
};

const ENROLL_URL = "https://shayamal.peak9.in/courses/performance-by-design?communityLink=shayamal&courseSlug=performance-by-design";

export default function ScienceOfExtraordinaryPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeShift, setActiveShift] = useState(0);
  
  const storyRef = useRef(null);
  const pricingRef = useRef(null);
  
  const isStoryInView = useInView(storyRef, { once: true, margin: "-100px" });
  const isPricingInView = useInView(pricingRef, { once: true, margin: "-100px" });

  // Course modules - Performance by Design curriculum
  const courseModules = [
    {
      id: "FOUNDATION",
      icon: Users,
      title: "The Undercover Athlete",
      coreIP: "The Learning Circle",
      time: "3 videos · ~20 min",
      description: "Before a single framework: the identity shift. Why you are already competing at athlete-level load, and why the course is designed the way it is."
    },
    {
      id: "MODULE 01",
      icon: Target,
      title: "Competence. Confidence. Composure.",
      coreIP: "The Calm → Podium Pyramid",
      time: "4 videos · ~35 min",
      description: "Where your ceiling actually sits, and the only mechanism that moves you up from it. Competence is knowledge. Confidence is experience. Composure is stillness."
    },
    {
      id: "MODULE 02",
      icon: Brain,
      title: "Signal vs. Noise",
      coreIP: "The Five Domains",
      time: "4 videos · ~35 min",
      description: "The five domains of performance noise, and how to find the one thing that moves everything else. AI gave everyone more information. It gave no one a better filter."
    },
    {
      id: "MODULE 03",
      icon: Heart,
      title: "Stillness Is a Skill",
      coreIP: "Shuhari · 守 破 離",
      time: "4 videos · ~35 min",
      description: "What stillness does neurologically — and why the best decisions are never made in the pressure moment, but in the recovery that follows it."
    },
    {
      id: "MODULE 04",
      icon: Zap,
      title: "Pressure Is a Signal",
      coreIP: "The 4-2-1 Gap System",
      time: "4 videos · ~35 min",
      description: "Why high stakes make capable people smaller — and the trained sequence that overrides it. Plus the diagnostic that tells you where your gap actually is."
    },
    {
      id: "MODULE 05",
      icon: Shield,
      title: "The 1% Ecosystem",
      coreIP: "Systems · Design · Lateral",
      time: "4 videos · ~30 min",
      description: "Insight without architecture is inspiration. Systems, design and lateral thinking — turning four modules into one personal operating system."
    }
  ];

  const builtForYou = [
    "You are capable, credentialled, and quietly aware that neither is the thing holding your ceiling in place.",
    "You operate under real consequence — money, people, reputation — on a schedule you do not fully control.",
    "You have noticed you become a smaller version of yourself in exactly the moments that decide things.",
    "You have consumed a great deal of leadership content and changed almost nothing as a result.",
    "You lead people who are doing the same thing, and you suspect it is costing the organisation more than anyone has measured."
  ];

  const notBuiltForYou = [
    "You want motivation. There is none here. It is a poor substitute for architecture and it wears off by Thursday.",
    "You are looking for more frameworks to collect. This course argues that collecting is the problem.",
    "You will watch and not write. The exercises are the mechanism — the videos are only the input.",
    "You want a certificate. You will not receive one, and the omission is deliberate."
  ];

  const learningCircle = [
    { icon: BookOpen, title: "Absorb", description: "One idea, taught clearly, with the science and the story behind it." },
    { icon: PenLine, title: "Reflect", description: "The prompt that makes it personal. Knowledge without reflection stays generic." },
    { icon: Share2, title: "Share", description: "Make the learning visible. When you articulate something, you own it." },
    { icon: MessageCircle, title: "Engage", description: "Test it against other people. Learning deepens under contact." }
  ];

  const bioPoints = [
    "25+ years in high performance — from elite athletes to C-suite leaders",
    "Stanford GSB teaching team, Neuroscience of Leadership",
    "Advisor across sport, corporate and health ecosystems",
    "Work at the intersection of AI, health-tech and performance science",
    "Author, sports scientist, and practitioner of Eastern philosophy"
  ];

  const faqs = [
    {
      q: "How long does it actually take?",
      a: "Three and a half hours of video. Perhaps four more if you do the exercises properly — and if you do not, the three and a half hours were wasted. Most people run it across two to three weeks, one module at a time, which is how it was designed to be consumed."
    },
    {
      q: "Is this just sports psychology relabelled for business?",
      a: "No — and the distinction matters. Sports psychology is largely built to help someone recover from a bad state. This is built to design an environment where the bad state is less likely to arrive, and where you have a trained sequence when it does anyway. The frameworks come from elite sport, but the application layer is entirely leadership."
    },
    {
      q: "I have done a lot of leadership development. What is different here?",
      a: "Most of it addressed competence — the level that was almost certainly never your constraint. This course argues that the accumulation itself has become the avoidance behaviour. If that lands uncomfortably, that discomfort is the signal you have been looking for."
    },
    {
      q: "Can I put my team through it?",
      a: "Yes, and the guides are built for it — every module has a \"leader as multiplier\" section with three specific team installs and a metric to track. For a structured cohort with facilitation and a measurement layer, that is Peak 9 corporate work; reply to any email and we will talk."
    },
    {
      q: "What if it does not work for me?",
      a: "Complete every module and every exercise. If nothing has changed, email me and I will refund you in full. I will not refund a course that was watched but not worked, because the work is the mechanism — refunding it would mean pretending the videos were the product."
    },
    {
      q: "Do I get a certificate?",
      a: "No. A certificate is competence theatre, and this entire course is an argument against it. What you get is a system, six guides you have written in, and a commitment you made in your own handwriting."
    }
  ];

  return (
    <main data-testid="science-extraordinary-page" className="bg-[#F4F1EC] overflow-x-hidden">
      {/* Floating CTA - Mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-gradient-to-t from-[#0f151c] to-[#0f151c]/95 lg:hidden">
        <a
          href={ENROLL_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          Enroll now — $299
          <ArrowRight size={16} />
        </a>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center bg-[#0f151c] overflow-hidden pt-20 lg:pt-0">
        <div className="container-peak relative z-10 py-12 lg:py-28 px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left">
              {/* Eyebrow */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-xs text-[#e17c18] font-['Syne'] uppercase tracking-[0.25em] mb-8"
              >
                Peak 9 · School of Shayamal
              </motion.p>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] mb-6 text-[#e8dcc7]"
              >
                Performance by Design
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-lg lg:text-xl text-[#e8dcc7]/80 mb-6 max-w-2xl italic font-['Cormorant_Garamond']"
              >
                The mental game is the last unfair advantage.
              </motion.p>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-[#e8dcc7]/60 text-base lg:text-lg mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0"
              >
                An athlete walking into a final and a leader walking into a board meeting run identical biology. Same cortisol. Same narrowed field. One of them has spent a career being prepared for that moment by professionals. The other has a calendar invite and a strong coffee.
              </motion.p>

              {/* Course Info */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="flex flex-wrap justify-center lg:justify-start gap-4 lg:gap-8 mb-8 text-sm"
              >
                <div>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-1">Format</p>
                  <p className="text-[#e8dcc7]">24 videos · 3.5 hours</p>
                </div>
                <div>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-1">Materials</p>
                  <p className="text-[#e8dcc7]">6 module field guides</p>
                </div>
                <div>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-1">Access</p>
                  <p className="text-[#e8dcc7]">Lifetime</p>
                </div>
              </motion.div>

              {/* CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-wrap justify-center lg:justify-start gap-4"
              >
                <a
                  href={ENROLL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center justify-center gap-3"
                >
                  Enroll now — $299
                  <ArrowRight size={18} />
                </a>
                <a
                  href="/diagnostic"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#e8dcc7]/30 text-[#e8dcc7] font-['Syne'] text-xs uppercase tracking-wider hover:border-[#e8dcc7]/60 transition-colors"
                >
                  Take the free diagnostic first
                </a>
              </motion.div>
            </div>

            {/* Right - Visual: Stairs with Three Pillars */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative flex justify-center lg:justify-end mt-8 lg:mt-0"
            >
              <svg viewBox="0 0 380 340" className="w-64 sm:w-72 lg:w-full lg:max-w-md">
                {/* Background circles */}
                <circle cx="220" cy="170" r="140" fill="none" stroke="#e17c18" strokeWidth="0.5" opacity="0.15" />
                <circle cx="220" cy="170" r="110" fill="none" stroke="#e17c18" strokeWidth="0.5" opacity="0.12" />
                <circle cx="220" cy="170" r="80" fill="none" stroke="#e17c18" strokeWidth="0.5" opacity="0.1" />
                
                {/* Three Steps */}
                <g stroke="#e8dcc7" strokeWidth="2" fill="none" opacity="0.8">
                  {/* Step 1 */}
                  <line x1="120" y1="270" x2="180" y2="270" />
                  <line x1="180" y1="270" x2="180" y2="210" />
                  {/* Step 2 */}
                  <line x1="180" y1="210" x2="240" y2="210" />
                  <line x1="240" y1="210" x2="240" y2="150" />
                  {/* Step 3 */}
                  <line x1="240" y1="150" x2="300" y2="150" />
                  <line x1="300" y1="150" x2="300" y2="90" />
                  <line x1="300" y1="90" x2="340" y2="90" />
                </g>

                {/* Labels arranged like stairs - staggered pattern */}
                
                {/* Pillar 1 - Neuroscience (bottom) */}
                <circle cx="180" cy="240" r="5" fill="#e17c18" />
                <line x1="180" y1="240" x2="130" y2="240" stroke="#e17c18" strokeWidth="1" opacity="0.4" />
                <text x="125" y="244" fill="#e8dcc7" opacity="0.7" textAnchor="end" style={{ fontSize: '8px', fontFamily: 'Syne', letterSpacing: '0.06em' }}>
                  NEUROSCIENCE
                </text>

                {/* Pillar 2 - Psychology (middle) - staggered up */}
                <circle cx="240" cy="180" r="5" fill="#e17c18" />
                <line x1="240" y1="180" x2="160" y2="180" stroke="#e17c18" strokeWidth="1" opacity="0.4" />
                <text x="155" y="184" fill="#e8dcc7" opacity="0.7" textAnchor="end" style={{ fontSize: '8px', fontFamily: 'Syne', letterSpacing: '0.06em' }}>
                  PSYCHOLOGY
                </text>

                {/* Pillar 3 - Behavioural Science (top) - staggered up more */}
                <circle cx="300" cy="120" r="5" fill="#e17c18" />
                <line x1="300" y1="120" x2="190" y2="120" stroke="#e17c18" strokeWidth="1" opacity="0.4" />
                <text x="185" y="124" fill="#e8dcc7" opacity="0.7" textAnchor="end" style={{ fontSize: '8px', fontFamily: 'Syne', letterSpacing: '0.06em' }}>
                  BEHAVIOURAL SCIENCE
                </text>

                {/* Person icon at top of stairs */}
                <circle cx="320" cy="70" r="10" fill="#e17c18" />

                {/* Labels */}
                <text x="110" y="295" fill="#e8dcc7" opacity="0.4" style={{ fontSize: '8px', fontFamily: 'Syne', letterSpacing: '0.1em' }}>
                  ORDINARY
                </text>
                <text x="320" y="45" fill="#e8dcc7" opacity="0.6" textAnchor="middle" style={{ fontSize: '8px', fontFamily: 'Syne', letterSpacing: '0.1em' }}>
                  EXTRAORDINARY
                </text>
              </svg>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div 
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <ChevronDown className="w-6 h-6 text-[#e8dcc7]/30" />
          </motion.div>
        </div>
      </section>

      {/* Image Reel + Letter Section */}
      <section className="py-16 lg:py-24 bg-white overflow-hidden">
        {/* Title with proper spacing */}
        <div className="container-peak">
          <p className="text-center text-[#5A6B7A] text-sm font-['Syne'] uppercase tracking-[0.2em] mb-12 lg:mb-16">
            25 years with the world's best
          </p>
        </div>

        {/* Auto-scrolling Image Reel - Full Color */}
        <div className="relative mb-16">
          <div className="flex overflow-hidden">
            <motion.div
              className="flex gap-4"
              animate={{ x: [0, -2400] }}
              transition={{ 
                duration: 35, 
                repeat: Infinity, 
                ease: "linear" 
              }}
            >
              {[...ATHLETE_PHOTOS, ...ATHLETE_PHOTOS].map((photo, i) => (
                <div key={i} className="flex-shrink-0 w-64 h-48 lg:w-80 lg:h-56 overflow-hidden rounded-sm">
                  <img 
                    src={photo} 
                    alt={`Shayamal with athlete ${i + 1}`}
                    className={`w-full h-full object-cover hover:scale-105 transition-transform duration-500 ${
                      photo === CENTER_POSITION_IMAGE ? 'object-center' : 'object-top'
                    }`}
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Letter Content */}
        <div className="container-peak">
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#F4F1EC] p-8 lg:p-12 border-l-4 border-[#e17c18]">
              <p className="text-[#5A6B7A] text-sm mb-6 font-['Cormorant_Garamond'] italic">A note from Shayamal</p>
              
              <div className="space-y-4 text-[#202b37] leading-relaxed">
                <p>
                  For 25 years, I've had the privilege of working with some of the world's best performers — Olympians, Grand Slam winners, the Indian Cricket Team, Fortune 500 leaders.
                </p>
                <p>
                  I've sat with them before the biggest moments of their lives. I've seen what happens inside their minds when everything is on the line.
                </p>
                <p className="text-lg font-['Cormorant_Garamond']">
                  And I've learned something most people never get to see:
                </p>
                <p className="text-2xl lg:text-3xl font-['Cormorant_Garamond'] text-[#e17c18]">
                  Extraordinary is not a gift. It is a science. And it is learnable.
                </p>
                <p>
                  I created this course to transfer the exact science I've used with the world's best — directly to you. No fluff, no motivation, just the system that works.
                </p>
                <p className="text-[#5A6B7A] italic">
                  If you're ready to learn what separates the extraordinary from everyone else — I'll see you inside.
                </p>
              </div>

              <div className="mt-8 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#e17c18]/30">
                  <img 
                    src={SHAYAMAL_IMAGE} 
                    alt="Shayamal" 
                    className="w-full h-full object-cover object-top scale-110" 
                  />
                </div>
                <div>
                  <p className="text-[#202b37] font-medium">Shayamal Vallabhjee</p>
                  <p className="text-[#5A6B7A] text-sm">Human Performance Designer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Premise */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-3xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">The Premise</p>
            
            <h2 className="text-2xl lg:text-3xl font-light text-[#202b37] mb-8">
              You have been performing without the science.
            </h2>

            <div className="space-y-6 text-[#5A6B7A] leading-relaxed">
              <p>
                You are already competing. You have been for years — in rooms where the stakes were real, where a single conversation moved a number that moved a company.
              </p>
              <p>
                The biology does not distinguish between a final and a board meeting. The preparation does. Elite athletes have entire professions built around getting them ready for the moment itself. You were handed a title and told to figure it out.
              </p>
              <p>
                This is not a leadership course. It does not give you more frameworks for a problem that was never a knowledge problem. It gives you what the 1% have that nobody taught you — and a system for installing it that survives past week two.
              </p>
              <p className="text-[#202b37] font-medium">
                Twenty-five years inside elite sport, translated for the boardroom. No motivation. No hustle. The science.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Split - 90 / 10 / 1 */}
      <section className="py-20 lg:py-28 bg-[#0f151c]">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6 text-center">The Split</p>
            
            <h2 className="text-3xl lg:text-4xl font-light text-[#e8dcc7] mb-16 text-center">
              Ninety. Ten. One.
            </h2>

            <div className="grid md:grid-cols-3 gap-8">
              {/* 90% */}
              <div className="text-center">
                <p className="text-5xl lg:text-6xl text-[#e17c18] font-['Cormorant_Garamond'] mb-4">90%</p>
                <p className="text-[#e8dcc7] font-medium mb-3">have competence</p>
                <p className="text-[#e8dcc7]/60 text-sm leading-relaxed">
                  Certifications, credentials, frameworks. The entire education industry exists to serve this group and to keep them here. More tools. More content. More knowing. AI just handed everyone else yours.
                </p>
              </div>

              {/* 10% */}
              <div className="text-center">
                <p className="text-5xl lg:text-6xl text-[#e17c18] font-['Cormorant_Garamond'] mb-4">10%</p>
                <p className="text-[#e8dcc7] font-medium mb-3">have confidence</p>
                <p className="text-[#e8dcc7]/60 text-sm leading-relaxed">
                  Because they applied it somewhere it could have failed publicly. Confidence is not generated by thinking. It is deposited by exposure.
                </p>
              </div>

              {/* 1% */}
              <div className="text-center">
                <p className="text-5xl lg:text-6xl text-[#e17c18] font-['Cormorant_Garamond'] mb-4">1%</p>
                <p className="text-[#e8dcc7] font-medium mb-3">have composure</p>
                <p className="text-[#e8dcc7]/60 text-sm leading-relaxed">
                  The ability to think with precision and find the point of leverage when everything is on the line. Not the absence of pressure. Clarity inside it.
                </p>
              </div>
            </div>

            <p className="text-center text-[#e8dcc7]/70 mt-12 italic">
              Almost everything being sold to you is more competence. <span className="text-[#e17c18]">More tools is not what you need.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Logo Bar - All credibility logos including Stanford */}
      <section className="py-8 bg-white border-y border-[#E6E1DA]">
        <div className="container-peak">
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12 opacity-50 hover:opacity-70 transition-all duration-500">
            {Object.entries(LOGOS).map(([name, url]) => (
              <img key={name} src={url} alt={name} className="h-6 lg:h-8 object-contain" />
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">Fit</p>
            <h2 className="text-2xl lg:text-3xl font-light text-[#202b37] mb-12">
              Who this is built for
            </h2>

            <div className="grid lg:grid-cols-2 gap-12">
              {/* Built for you */}
              <div>
                <p className="text-[#202b37] font-medium mb-6">Built for you if:</p>
                <ul className="space-y-4">
                  {builtForYou.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-[#e17c18] mt-0.5 shrink-0" />
                      <span className="text-[#5A6B7A] text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not built for you */}
              <div>
                <p className="text-[#202b37] font-medium mb-6">Not built for you if:</p>
                <ul className="space-y-4">
                  {notBuiltForYou.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <X className="w-5 h-5 text-[#5A6B7A]/50 mt-0.5 shrink-0" />
                      <span className="text-[#5A6B7A] text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Curriculum */}
      <section ref={storyRef} className="py-20 lg:py-28 bg-white">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">The Curriculum</p>
            <h2 className="text-2xl lg:text-3xl font-light text-[#202b37] mb-4">
              One foundation. Five modules. One system.
            </h2>
            <p className="text-[#5A6B7A] mb-12 max-w-2xl">
              Twenty-four videos, three to seven minutes each. One idea per video — never two. Every module closes with an exercise, a 72-hour action, and a field guide you write in.
            </p>

            {/* Modules - Expandable */}
            <div className="space-y-3">
              {courseModules.map((module, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isStoryInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className={`border cursor-pointer transition-all ${
                    activeShift === i 
                      ? 'border-[#e17c18] bg-[#0f151c]' 
                      : 'border-[#E6E1DA] hover:border-[#e17c18]/50'
                  }`}
                  onClick={() => setActiveShift(activeShift === i ? null : i)}
                >
                  <div className="p-5 lg:p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="lg:w-28 shrink-0">
                        <p className={`text-xs font-['Syne'] uppercase tracking-[0.15em] ${
                          activeShift === i ? 'text-[#e17c18]' : 'text-[#5A6B7A]'
                        }`}>
                          {module.id}
                        </p>
                      </div>
                      <div className="flex-1">
                        <h3 className={`text-lg font-medium ${
                          activeShift === i ? 'text-[#e8dcc7]' : 'text-[#202b37]'
                        }`}>
                          {module.title}
                        </h3>
                        {activeShift === i && (
                          <motion.p
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="text-[#e8dcc7]/70 text-sm mt-3 leading-relaxed"
                          >
                            {module.description}
                          </motion.p>
                        )}
                      </div>
                      <div className="lg:w-48 shrink-0 lg:text-right">
                        <p className={`text-xs ${activeShift === i ? 'text-[#e17c18]' : 'text-[#5A6B7A]'}`}>
                          {module.coreIP}
                        </p>
                        <p className={`text-xs mt-1 ${activeShift === i ? 'text-[#e8dcc7]/50' : 'text-[#5A6B7A]/50'}`}>
                          {module.time}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Field Guides */}
      <section className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">What most courses do not give you</p>
            <h2 className="text-2xl lg:text-3xl font-light text-[#202b37] mb-4">
              Six field guides. Written to be written in.
            </h2>
            <p className="text-[#5A6B7A] mb-12 max-w-2xl">
              Every module ships with a designed guide — the framework diagram, the science translated for a leadership context, worksheets, a 72-hour action, and a reading list that is short because most of the alternatives are not worth your time. Print them. Write in them. They are the difference between a course you watched and a system you installed.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white p-6 border border-[#E6E1DA]">
                <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-wider mb-3">01</p>
                <h3 className="text-lg text-[#202b37] font-medium mb-2">The framework, drawn</h3>
                <p className="text-[#5A6B7A] text-sm">Every model as a diagram you can put on a whiteboard from memory.</p>
              </div>
              <div className="bg-white p-6 border border-[#E6E1DA]">
                <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-wider mb-3">02</p>
                <h3 className="text-lg text-[#202b37] font-medium mb-2">The work, structured</h3>
                <p className="text-[#5A6B7A] text-sm">Exercises with room to write. Analogue on purpose — handwriting forces selection.</p>
              </div>
              <div className="bg-white p-6 border border-[#E6E1DA]">
                <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-wider mb-3">03</p>
                <h3 className="text-lg text-[#202b37] font-medium mb-2">Leader as multiplier</h3>
                <p className="text-[#5A6B7A] text-sm">Three specific moves for installing each module in your team, and the metric to track it.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Works - The Learning Circle */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">Why it works</p>
            <h2 className="text-2xl lg:text-3xl font-light text-[#202b37] mb-4">
              The Learning Circle
            </h2>
            <p className="text-[#5A6B7A] mb-12 max-w-2xl">
              Most courses stop at the first step. That is why enthusiasm is the only measurable outcome they produce. Complete all four on any idea and it stops being content you consumed — it becomes part of how you operate.
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {learningCircle.map((step, i) => (
                <div key={i} className="bg-[#F4F1EC] p-6 border border-[#E6E1DA]">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#e17c18]/30 mb-4">
                    <step.icon className="w-5 h-5 text-[#e17c18]" />
                  </div>
                  <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-wider mb-2">0{i + 1}</p>
                  <h3 className="text-lg text-[#202b37] font-medium mb-2">{step.title}</h3>
                  <p className="text-[#5A6B7A] text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who Built This - Credibility Bio */}
      <section className="py-20 lg:py-28 bg-[#1F2A44]">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-4xl mx-auto grid lg:grid-cols-[auto_1fr] gap-10 lg:gap-16 items-start">
            <div className="flex-shrink-0 mx-auto lg:mx-0">
              <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-2 border-[#e17c18]/40">
                <img
                  src={SHAYAMAL_IMAGE}
                  alt="Shayamal Vallabhjee"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">Who built this</p>
              <h2 className="text-2xl lg:text-3xl font-light text-[#F4F1EC] mb-1">
                Shayamal Vallabhjee
              </h2>
              <p className="text-[#F4F1EC]/50 text-sm font-['Syne'] uppercase tracking-wider mb-6">
                Human Performance Designer
              </p>
              <p className="text-xl lg:text-2xl font-['Cormorant_Garamond'] text-[#F4F1EC]/90 italic mb-8">
                "I do not coach. I design systems that make performance inevitable."
              </p>

              <ul className="space-y-3 mb-8 text-left max-w-md mx-auto lg:mx-0">
                {bioPoints.map((point, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#F4F1EC]/70 text-sm leading-relaxed">
                    <CheckCircle className="w-4 h-4 text-[#e17c18] mt-0.5 shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>

              <p className="text-[#F4F1EC]/60 text-sm leading-relaxed max-w-xl mx-auto lg:mx-0">
                The reason I can teach this is not academic. I have been in the room with people whose performance was measured publicly, in real time, with nowhere to hide — and I watched what actually separated the ones who rose from the ones who did not. It was never the talent. It was never the knowledge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Undercover Athletes - Moved above pricing */}
      <section className="py-20 lg:py-28 bg-[#202b37]">
        <div className="container-peak">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-light text-[#e8dcc7] mb-8">
                I am building a team of{' '}
                <em className="not-italic text-[#e17c18]">Undercover Athletes</em>.
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-3 text-left mt-10 max-w-2xl mx-auto">
                {[
                  "Think like the world's greatest athletes think.",
                  "Prepare like they prepare.",
                  "Perform under pressure the way they perform.",
                  "Recover, adapt, and keep raising their standard."
                ].map((text, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3 p-4 border border-[#e8dcc7]/10"
                  >
                    <CheckCircle className="w-4 h-4 text-[#e17c18] mt-1 shrink-0" />
                    <p className="text-[#e8dcc7]/80 text-sm">{text}</p>
                  </motion.div>
                ))}
              </div>

              <p className="text-[#e8dcc7]/60 mt-10 max-w-xl mx-auto">
                These are the people who will thrive in the AI world. Not because they learned another tool. But because they built a mind that <span className="text-[#e17c18]">no tool can replace</span>.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={pricingRef} id="enroll" className="py-20 lg:py-28 bg-[#0f151c]">
        <div className="container-peak">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isPricingInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">The offer</p>
              <h2 className="text-2xl lg:text-4xl font-light text-[#e8dcc7]">
                Enroll today
              </h2>
            </div>

            <div className="max-w-lg mx-auto">
              <div className="bg-[#1a2330] border border-[#e17c18] p-8 lg:p-10">
                <div className="text-center border-b border-[#e8dcc7]/10 pb-6 mb-6">
                  <p className="text-5xl text-[#e17c18] font-['Syne'] mb-2">$299</p>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mt-2">Lifetime access</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {[
                    "All 24 videos across the foundation and five modules — lifetime access",
                    "Six designed field guides, print-ready, with every framework and worksheet",
                    "The Vision Document and Commitment Submission bookends",
                    "Membership of the School of Shayamal community",
                    "The Pay It Forward code on completion — pass the course to someone who needs it"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#e8dcc7]/70 text-sm">
                      <CheckCircle className="w-4 h-4 text-[#e17c18] mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={ENROLL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  Enroll now
                  <ArrowRight size={16} />
                </a>

                <p className="text-[#e8dcc7]/50 text-xs text-center mt-6 leading-relaxed">
                  If you complete every module and every exercise and it changes nothing, email me and I will refund you in full. That offer is conditional on doing the work, because the work is the product.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Diagnostic CTA */}
      <section className="py-16 lg:py-20 bg-[#202b37]">
        <div className="container-peak px-4 sm:px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-xl lg:text-2xl font-light text-[#e8dcc7] mb-4">
              Not sure yet? Find the gap first.
            </h3>
            <p className="text-[#e8dcc7]/60 mb-8">
              Twelve questions. At the end you will know which of three things is actually holding your ceiling in place — your body, your strategy, or your mind. Most people are solving the wrong one, harder. It is free, it takes three minutes, and you are under no obligation to do anything with it.
            </p>
            <a
              href="/diagnostic"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#e17c18] text-[#e17c18] font-['Syne'] text-xs uppercase tracking-wider hover:bg-[#e17c18] hover:text-white transition-all"
            >
              Take the 1% Gap Diagnostic
              <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="max-w-3xl mx-auto">
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-6">Before you decide</p>
            <h2 className="text-2xl lg:text-3xl font-light text-[#202b37] mb-12">
              Honest answers
            </h2>

            <div className="space-y-0 border-t border-[#E6E1DA]">
              {faqs.map((faq, i) => (
                <div key={i} className="border-b border-[#E6E1DA]">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full py-6 flex items-center justify-between text-left"
                  >
                    <span className="text-[#202b37] font-medium pr-4">{faq.q}</span>
                    <ChevronDown className={`w-5 h-5 text-[#5A6B7A] transition-transform shrink-0 ${
                      openFaq === i ? 'rotate-180' : ''
                    }`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="text-[#5A6B7A] pb-6 leading-relaxed max-w-2xl">
                          {faq.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-[#0f151c]">
        <div className="container-peak">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-2xl lg:text-3xl font-['Cormorant_Garamond'] text-[#e8dcc7]/80 italic mb-8">
              The science does not care about your potential.<br />
              It only responds to your practice.
            </p>
            
            <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.2em] mb-10">
              Shayamal Vallabhjee
            </p>

            <a
              href={ENROLL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-3 text-base px-12 py-5"
            >
              Enroll now — $299
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Bottom spacer for mobile fixed CTA */}
      <div className="h-24 lg:hidden bg-[#0f151c]" />
    </main>
  );
}
